import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { VariantRow } from '../types';
import { fmtInt, fmtMoney, fmtPct, fmtRatio } from '../utils';

export interface VariantsRequest {
  adId: string | null;
  adName: string;
  assetType: 'headline' | 'primary_text';
  // The variants stored on the creative. Always present, so the dialog can list
  // the rotation even when Meta reported no per-asset delivery for the window.
  storedVariants: string[];
}

interface Props {
  request: VariantsRequest | null;
  rows: VariantRow[];
  loading: boolean;
  onClose: () => void;
}

const numberSx = { fontVariantNumeric: 'tabular-nums' as const };

// Advantage+ rotates several headlines and bodies under one ad. This shows the
// whole rotation, and — where Meta broke delivery out by asset — what each one
// actually earned, rather than just how many there were.
export default function VariantsDialog({ request, rows, loading, onClose }: Props) {
  const label = request?.assetType === 'headline' ? 'Headlines' : 'Primary text';

  // Anything Meta reported on comes first, with its numbers; variants it never
  // delivered are still listed so the count matches what the row claimed.
  const measured = new Set(
    rows.map(row => (row.asset_text || '').trim()).filter(Boolean),
  );
  const unmeasured = (request?.storedVariants || []).filter(
    text => text && !measured.has(text.trim()),
  );

  return (
    <Dialog open={!!request} onClose={onClose} maxWidth="md" fullWidth>
      {request && (
        <>
          <DialogTitle sx={{ pr: 6, fontSize: 18 }}>
            {label} in this ad
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
              {request.adName}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                {rows.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{label.replace(/s$/, '')}</TableCell>
                        <TableCell align="right">Spend</TableCell>
                        <TableCell align="right">Meta ROAS</TableCell>
                        <TableCell align="right">Purchases</TableCell>
                        <TableCell align="right">CTR</TableCell>
                        <TableCell align="right">Impr.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow key={row.asset_id} hover>
                          <TableCell sx={{ maxWidth: 380 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {row.asset_text || '(no text)'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={numberSx}>
                            {fmtMoney(row.spend)}
                          </TableCell>
                          <TableCell align="right" sx={numberSx}>
                            {fmtRatio(row.meta_roas)}
                          </TableCell>
                          <TableCell align="right" sx={numberSx}>
                            {fmtInt(row.purchases)}
                          </TableCell>
                          <TableCell align="right" sx={numberSx}>
                            {fmtPct(row.ctr, 2)}
                          </TableCell>
                          <TableCell align="right" sx={numberSx}>
                            {fmtInt(row.impressions)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {unmeasured.length > 0 && (
                  <Box sx={{ mt: rows.length > 0 ? 3 : 0 }}>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      {rows.length > 0
                        ? 'Also in the rotation, with no delivery reported in this window:'
                        : 'In the rotation. Meta reported no per-asset delivery in this window.'}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 1, mb: 0 }}>
                      {unmeasured.map(text => (
                        <Typography key={text} component="li" variant="body2" sx={{ mb: 0.5 }}>
                          {text}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {rows.length === 0 && unmeasured.length === 0 && (
                  <Alert severity="info">
                    No variants recorded for this ad in the selected date range.
                  </Alert>
                )}
              </>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}
